import { useState } from "react";

export const RESOLUTIONS = {
	SKIP: "skip",
	OVERWRITE: "overwrite",
	RENAME: "rename",
};

function generateInitialResolutionState(conflicts = []){
	return conflicts.reduce((prev, conflict) => {
		prev[conflict.name] = {
			resolution: RESOLUTIONS.SKIP,
		};
		return prev;
	}, {});
}

export default function useConflictResolver(conflicts){
	/*
		This hook just encapsulates some state and methods to track how the user wants to resolve
		a batch of upload conflicts.

		`conflicts` is expected to be an array of objects with a uniquely identifying `name` property.

		The hook returns an object with a `resolutions` hash, keyed with each conflict name, where the
		values are in the form:

		{
			resolution, // string identifying the selected resolution, defaults to RESOLUTIONS.SKIP
			newName, // If the resolution is RESOLUTIONS.RENAME, this is the new name to use
		}

		The returned object will also have the following keyed functions that will apply the corresponding
		resolutions with the specified names:
			skip(names){}
			overwrite(names){}
			rename(namePairs){}
		}


		Example usage:
		```
			let conflicts = [
				{ name: "video1.mp4" },
				{ name: "video2.mp4" },
				{ name: "video3.mp4" },
				{ name: "video4.mp4" },
			];

			function UploadComponent(){
				let resolver = useConflictResolver(conflicts);

				resolver.skip(["video1.mp4", "video2.mp4"]);
				resolver.overwrite(["video3.mp4"]);
				resolver.rename([
					["video4.mp4", "video4_copy.mp4"]
				]);

				...
			}
		```

		`resolver.resolutions` would end up with the value:
			{
				"video1.mp4": {
					resolution: "skip",
				},
				"video2.mp4": {
					resolution: "skip",
				},
				"video3.mp4": {
					resolution: "overwrite",
				},
				"video4.mp4": {
					resolution: "rename",
					newName: "video4_copy.mp4",
				},
			}

		This hash can then be referenced as needed to build out whatever structures and requests
		are necessary to process your uploads.
	*/
	const [resolutions, setResolutions] = useState(generateInitialResolutionState(conflicts));

	function skip(names){
		let newResolutions = names.reduce((prev, name) => {
			prev[name] = {
				resolution: RESOLUTIONS.SKIP,
			};
			return prev;
		}, { ...resolutions });

		setResolutions(newResolutions);
	}

	function overwrite(names){
		let newResolutions = names.reduce((prev, name) => {
			prev[name] = {
				resolution: RESOLUTIONS.OVERWRITE,
			};
			return prev;
		}, { ...resolutions });

		setResolutions(newResolutions);
	}

	function rename(namePairs){
		/*
			`namePairs` is expected to be an array of 2 element arrays in the form:
			[originalName, newName]
		*/
		let newResolutions = namePairs.reduce((prev, [name, newName]) => {
			prev[name] = {
				resolution: RESOLUTIONS.RENAME,
				newName,
			};
			return prev;
		}, { ...resolutions });

		setResolutions(newResolutions);
	}

	return {
		resolutions,

		skip,
		overwrite,
		rename,
	};
}
